/**
 * The CRC-32 ZIP uses (ISO 3309), shared by the bundle's writer (part 24a, table Z1) and the archive reader (part 17,
 * table R10). Moved here from the export package in part 17, unchanged, so both sides compute one checksum.
 */

/**
 * Eight CRC tables of 256 entries each, as little-endian 32-bit words (slicing by 8): table 0 is a byte's CRC, table k
 * the CRC of that byte followed by k zero bytes. Held in a `DataView`, whose reads are plain numbers, so the loop needs no
 * bounds check of its own (part 16c: a book's images, up to 100 MiB, are checksummed whole).
 */
const CRC_TABLES = new DataView(new ArrayBuffer(8 * 256 * 4));
for (let n = 0; n < 256; n += 1) {
  let c = n;
  for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb8_8320 ^ (c >>> 1) : c >>> 1;
  CRC_TABLES.setInt32(n * 4, c, true);
}
for (let table = 1; table < 8; table += 1) {
  for (let n = 0; n < 256; n += 1) {
    const before = CRC_TABLES.getInt32(((table - 1) * 256 + n) * 4, true);
    CRC_TABLES.setInt32((table * 256 + n) * 4, (before >>> 8) ^ CRC_TABLES.getInt32((before & 0xff) * 4, true), true);
  }
}

/** Entry `n` (0–255) of CRC table `table` (0–7). */
function crcEntry(table: number, n: number): number {
  return CRC_TABLES.getInt32((table * 256 + n) * 4, true);
}

/** The CRC-32 of `bytes` (ISO 3309, as ZIP uses it): eight bytes a step, then one at a time. */
export function crc32(bytes: Uint8Array): number {
  const data = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const whole = bytes.length - (bytes.length % 8);
  let crc = -1;
  let i = 0;
  for (; i < whole; i += 8) {
    const low = crc ^ data.getInt32(i, true);
    const high = data.getInt32(i + 4, true);
    crc =
      crcEntry(7, low & 0xff) ^
      crcEntry(6, (low >>> 8) & 0xff) ^
      crcEntry(5, (low >>> 16) & 0xff) ^
      crcEntry(4, low >>> 24) ^
      crcEntry(3, high & 0xff) ^
      crcEntry(2, (high >>> 8) & 0xff) ^
      crcEntry(1, (high >>> 16) & 0xff) ^
      crcEntry(0, high >>> 24);
  }
  for (; i < bytes.length; i += 1) crc = crcEntry(0, (crc ^ data.getUint8(i)) & 0xff) ^ (crc >>> 8);
  return (crc ^ -1) >>> 0;
}
